import type { JsonSchema, JsonSchemaProperty } from '@shared/types'
import { forEach, map } from 'lodash'
import type { RExternalAppDetails, RExternalOrgAppConnection } from 'v2/types/api/settings'
import { z } from 'zod'

export const buildNestedFieldValidation = (
  category: string,
  fieldLabel: string,
  isRequired: boolean,
) => {
  let validation = z.string()
  if (isRequired) {
    validation = validation.min(1, `${fieldLabel} is required`)
  }
  if (category === 'connection') {
    validation = validation.regex(/^\S*$/, `${fieldLabel} cannot contain spaces`)
  }
  return isRequired ? validation : validation.optional()
}

export const getOptionsFromEnum = (enumValue: unknown) => {
  if (Array.isArray(enumValue)) {
    return map(enumValue, (value: unknown, index: number) => ({
      label: value,
      value: `${index}:${String(value)}`,
    }))
  }

  if (enumValue && typeof enumValue === 'object') {
    return map(Object.entries(enumValue), ([label, value], index) => ({
      label,
      value: `${index}::${String(value)}`,
    }))
  }

  return []
}

export const extractEnumValue = (value: unknown): unknown => {
  if (typeof value !== 'string') return value
  const indexedValue = value.match(/^\d+:{1,2}(.*)$/s)
  return indexedValue?.[1] ?? value
}

export const extractFormValue = (schema: JsonSchema | undefined, key: string, value: unknown) => {
  const [category, field] = key.split('__')
  const categoryProperty = schema?.properties?.[category]
  const fieldProperty =
    (category === 'connection' || category === 'credentials') && field
      ? categoryProperty?.properties?.[field]
      : categoryProperty

  return fieldProperty && 'enum' in fieldProperty ? extractEnumValue(value) : value
}

export const flattenSchemaDefaults = (schema: JsonSchema): Record<string, unknown> => {
  const defaults: Record<string, unknown> = {}
  if (!schema?.properties) return defaults

  const processNestedDefaults = (category: string, categoryData: JsonSchemaProperty) => {
    if (!categoryData?.properties || typeof categoryData.properties !== 'object') return

    forEach(
      Object.entries(categoryData.properties),
      ([field, fieldProps]: [string, JsonSchemaProperty]) => {
        if (fieldProps.defaultValue !== undefined && !fieldProps.hidden) {
          defaults[`${category}__${field}`] = fieldProps.defaultValue
        }
      },
    )
  }

  if (schema.properties.connection) {
    processNestedDefaults('connection', schema.properties.connection)
  }
  if (schema.properties.credentials) {
    processNestedDefaults('credentials', schema.properties.credentials)
  }

  forEach(
    Object.entries(schema.properties),
    ([category, categoryProps]: [string, JsonSchemaProperty]) => {
      if (category === 'connection' || category === 'credentials') return
      if (
        typeof categoryProps === 'object' &&
        categoryProps !== null &&
        categoryProps.type &&
        categoryProps.defaultValue !== undefined &&
        !categoryProps.hidden
      ) {
        defaults[category] = categoryProps.defaultValue
      }
    },
  )

  return defaults
}

export interface PreparedConnectionDetails {
  connectionDetails: RExternalOrgAppConnection
  otherDetails: Record<string, unknown>
}

export const prepareConnectionDetails = (
  values: Record<string, unknown>,
  app: RExternalAppDetails,
): PreparedConnectionDetails => {
  const connectionDetails: RExternalOrgAppConnection = { connection: {}, credentials: {} }
  const otherDetails: Record<string, unknown> = {}

  forEach(Object.entries(values), ([key, value]) => {
    const [category, field] = key.split('__')
    const actualValue = extractFormValue(app.input_schema as JsonSchema | undefined, key, value)
    if ((category === 'connection' || category === 'credentials') && field) {
      connectionDetails[category][field] = actualValue
      return
    }
    otherDetails[key] = actualValue
  })

  const properties = app.input_schema?.properties
  forEach(['connection', 'credentials'] as const, (category) => {
    const categoryProperties = properties?.[category]?.properties
    if (!categoryProperties) return
    forEach(Object.entries(categoryProperties), ([field, fieldProperties]) => {
      if (fieldProperties.hidden && 'defaultValue' in fieldProperties) {
        connectionDetails[category][field] = fieldProperties.defaultValue
      }
    })
  })

  forEach(Object.entries(properties ?? {}), ([category, categoryProperties]) => {
    if (category === 'connection' || category === 'credentials') return
    if (categoryProperties.hidden && 'defaultValue' in categoryProperties) {
      otherDetails[category] = categoryProperties.defaultValue
    }
  })

  return { connectionDetails, otherDetails }
}
